import { Component, inject, OnInit, signal } from '@angular/core';
import { faMailBulk, faPhone, faSms } from "@fortawesome/free-solid-svg-icons";
import { Validators, ReactiveFormsModule, FormsModule, FormBuilder } from "@angular/forms";
import { Customer, Technician } from "../../_models";
import { Role } from "../../_models/role.interface";
import { CustomerService, UserService } from "../../_services";
import { RoleService } from "../../_services/role.service";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { TechnicianUpdateHash } from "../../_models/technician.interface";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import * as mapboxgl from "mapbox-gl";
import { PAUtil } from "../planning-assistant/classes/util";
import { MapBoxService } from "../../_services/mapbox.service";
import { GeocoderEvent } from "../../_models/mapbox.interface";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CommonModule } from "@angular/common";
import { RecordNavigationComponent } from "../_shared/record-navigation/record-navigation.component";
import { StorePickerComponent } from "../_shared/store-picker/store-picker.component";
import { ColorPickerComponent } from "../_shared/color-picker/color-picker.component";
import { TechnicianStockLevelsGridComponent } from '../technician-stock-levels/technician-stock-levels-grid.component';
import { TechnicianStockLevelDraft, TechnicianStockLevelService } from '../../_services/technician-stock-level.service';
import { DISPATCHER_COLORS, TOUR_COLORS } from "../../_helpers/color-palettes";
import { StorePickerStore } from "../../_models/store.interface";
import { INTERNAL_SHIPPING_PROJECT_ID } from "../../_models/internal-project.const";
import { isBentomaxTechnician } from "../../_helpers/bentomax-technician";
import { httpErrorMessage } from "../../_helpers/http-error-message";
import { applyGeocoderAddress } from "../../_helpers/geocoder-address";

@Component({
  selector: 'hawk-user-create',
  templateUrl: './user-create.component.html',
  styleUrls: ['./user-create.component.scss'],
  standalone: true,
  imports: [RecordNavigationComponent, ReactiveFormsModule, FontAwesomeModule, FormsModule, CommonModule, FontAwesomeModule, RouterLink, StorePickerComponent, ColorPickerComponent, TechnicianStockLevelsGridComponent],
  host: {
    class: 'app-page'
  }
})
export class UserCreateComponent implements OnInit {

  protected readonly faSms = faSms;
  protected readonly faPhone = faPhone;
  protected readonly faMailBulk = faMailBulk;
  private fb = inject(FormBuilder)
  private stockLevelService = inject(TechnicianStockLevelService)
  customers: Customer[] = []
  roles: Role[] = []

  /**
   * Mindest-/Sollbestände, die im Anlegen-Dialog zusammengestellt wurden. Sie
   * tragen noch keine Nutzer-ID — die gibt es erst nach dem Erstellen, deshalb
   * werden sie dort nachgereicht.
   */
  stockLevelDrafts: TechnicianStockLevelDraft[] = []
  checkedRoles: {role: Role, checked: boolean}[] = []
  subcontractors: Technician[] = []
  sendAccount: boolean = false
  addressGeocoder?: MapboxGeocoder
  companyAddressGeocoder?: MapboxGeocoder

  protected readonly internalShippingProjectId = INTERNAL_SHIPPING_PROJECT_ID
  protected internalShippingStore = signal<StorePickerStore | null>(null)
  // Feste Tourfarbe für Dashboard und Planungsassistent; ohne Wert greift die Index-Farbe.
  protected tourColor = signal<string | null>(null)
  protected readonly tourColorPalette = TOUR_COLORS
  // Feste Dispatcher-Farbe; ohne Wert vergibt das Dashboard sie nach Index.
  protected dispatcherColor = signal<string | null>(null)
  protected readonly dispatcherColorPalette = DISPATCHER_COLORS
  protected showStorePicker = signal<boolean>(false)

  // Speicherfehler (z.B. 422 wegen bereits vergebener Versandfiliale) — wird
  // über dem Erstellen-Button angezeigt; navigiert wird nur bei Erfolg.
  protected saveError: string | null = null

  userForm = this.fb.group({
    first_name: this.fb.control<string>("", Validators.required),
    last_name: this.fb.control<string>("", Validators.required),
    company: this.fb.control<string>(""),
    repairer: this.fb.control<string>(""),
    email1: this.fb.control<string>("", [Validators.email, Validators.required]),
    email2: this.fb.control<string>(""),
    email_moreapp: this.fb.control<string>("", Validators.email),
    email_partner1: this.fb.control<string>("", Validators.email),
    email_partner2: this.fb.control<string>(""),
    customer_id: this.fb.control<number>(null),
    employer_id: this.fb.control<number>(null),
    phone1: this.fb.control<string>('', Validators.pattern('[- +()0-9]+')),
    phone2: this.fb.control<string>(''),
    telephone_dispatching: this.fb.control<string>('', Validators.pattern('[- +()0-9]+')),
    street: this.fb.control<string>({value: '', disabled: true}),
    zip: this.fb.control<string>({value: '', disabled: true}, [Validators.pattern('^([a-zA-Z]{2}-)?[0-9]{2,5}( [a-zA-Z]{0,2})?$')]),
    city: this.fb.control<string>({value: '', disabled: true}),
    country: this.fb.control<string>({value: '', disabled: true}),
    state: this.fb.control<string>({value: '', disabled: true}),
    lat: this.fb.control<number>(null),
    lng: this.fb.control<number>(null),
    company_address_company: this.fb.control<string>(""),
    company_address_country: this.fb.control<string>({value: '', disabled: true}),
    company_address_state: this.fb.control<string>({value: '', disabled: true}),
    company_address_street: this.fb.control<string>({value: '', disabled: true}),
    company_address_zip: this.fb.control<string>({value: '', disabled: true}, [Validators.pattern('^([a-zA-Z]{2}-)?[0-9]{2,5}( [a-zA-Z]{0,2})?$')]),
    company_address_city: this.fb.control<string>({value: '', disabled: true}),
    card_nr_packstation: this.fb.control<string>(""),
    pickpoint_nr: this.fb.control<string>(""),
    xml_id: this.fb.control<string>(""),
    password: this.fb.control<string>("", Validators.required),
    comment: this.fb.control<string>("")
  });

  constructor(
    public userService: UserService,
    public customerService: CustomerService,
    public roleService: RoleService,
    public route: ActivatedRoute,
    private router: Router,
    private mapBoxService: MapBoxService
  ) {
  }

  ngOnInit(): void {
    this.resetForm()
    this.loadCustomers()
    this.loadRoles()
    this.loadSubcontractors()
    this.setGeocoder()
  }

  public async setGeocoder(): Promise<void> {
    this.addressGeocoder = new MapboxGeocoder({
      accessToken: this.mapBoxService.accessToken,
      marker: false,
      language: 'de-DE',
      mapboxgl: mapboxgl,
      placeholder: 'Adresse suchen'
    })
    this.companyAddressGeocoder = new MapboxGeocoder({
      accessToken: this.mapBoxService.accessToken,
      marker: false,
      language: 'de-DE',
      mapboxgl: mapboxgl,
      placeholder: 'Firmenadresse suchen'
    })
    while (!document.getElementById('address_geocoder') || !document.getElementById('company_address_geocoder')) {
      await PAUtil.sleep(100)
    }
    const address_geocoder_elem = document.getElementById('address_geocoder')
    const company_address_geocoder_elem = document.getElementById('company_address_geocoder')
    this.addressGeocoder.addTo(address_geocoder_elem);
    this.companyAddressGeocoder.addTo(company_address_geocoder_elem);

    this.addressGeocoder.on('result', (event) => {
      this.onAddressGeocoderEvent(event)
    })
    this.companyAddressGeocoder.on('result', (event) => {
      this.onCompanyAddressGeocoderEvent(event)
    })
  }

  private onAddressGeocoderEvent(event: GeocoderEvent) {
    const controls = this.userForm.controls

    applyGeocoderAddress(PAUtil.getGeocoderEventAddress(event), {
      country: controls.country,
      state: controls.state,
      city: controls.city,
      zip: controls.zip,
      street: controls.street,
      lat: controls.lat,
      lng: controls.lng
    })
    this.userForm.updateValueAndValidity()
  }

  // Die Firmenadresse kennt keine Koordinaten — lat/lng bleiben deshalb weg.
  private onCompanyAddressGeocoderEvent(event: GeocoderEvent) {
    const controls = this.userForm.controls

    applyGeocoderAddress(PAUtil.getGeocoderEventAddress(event), {
      country: controls.company_address_country,
      state: controls.company_address_state,
      city: controls.company_address_city,
      zip: controls.company_address_zip,
      street: controls.company_address_street
    })
    this.userForm.updateValueAndValidity()
  }

  loadCustomers(): void {
    this.customerService.loadCustomers().subscribe(
      customers => {
        this.customers = customers.sort((c1, c2) => c1.name.toLowerCase() < c2.name.toLowerCase() ? -1 : 1)
      },
      err => {
        console.log(err)
      }
    )
  }

  loadRoles(): void {
    this.roleService.getRoles().subscribe(
      roles => {
        this.roles = roles
        this.resetRoles()
      },
      err => {
        console.log(err)
      }
    )
  }

  loadSubcontractors(): void {
    this.userService.getSubcontractors().subscribe(
      subcontractors => {
        this.subcontractors = subcontractors
      },
      err => {
        console.log(err)
      }
    )
  }

  resetForm():void {
    this.userForm.setValue({
      first_name: '',
      last_name: '',
      company: '',
      repairer: '',
      email1: '',
      email2: '',
      email_moreapp: '',
      email_partner1: '',
      email_partner2: '',
      customer_id: null,
      employer_id: null,
      phone1: '',
      phone2: '',
      telephone_dispatching: '',
      street: '',
      zip: '',
      city: '',
      country: '',
      state: '',
      company_address_company: '',
      company_address_country: '',
      company_address_state: '',
      company_address_street: '',
      company_address_zip: '',
      company_address_city: '',
      password: '',
      lat: 0,
      lng: 0,
      card_nr_packstation: '',
      pickpoint_nr: '',
      xml_id: '',
      comment: ''
    });

    this.resetRoles()
  }

  resetRoles(): void {
    this.checkedRoles = this.roles.filter(r => !r.name.includes('CRM')).map(
      role => {
        return { role: role, checked: role.name == 'USER' }
      }
    )
  }

  async onSubmit() {
    if (this.missingInternalShippingStore && !confirm(
      'Für Bentomax-Techniker sollte eine Versandfiliale hinterlegt sein. Benutzer trotzdem ohne Versandfiliale anlegen?'
    )) {
      return
    }

    this.saveError = null
    const user_id = await this.createUser()
    if (user_id) {
      await this.createRoles(user_id)
      await this.createStockLevels(user_id)
      this.router.navigate(['/benutzer', user_id])
    }
  }

  /**
   * Speichert die vorgemerkten Bestände, sobald der Nutzer eine ID hat.
   *
   * Ein Fehler hier bricht das Anlegen NICHT ab: Der Nutzer existiert bereits,
   * und ihn wegen der Bestände zu verwerfen wäre schlimmer als die Bestände
   * nachzutragen. Stattdessen wird der Grund auf der Seite gemeldet.
   */
  private async createStockLevels(user_id: number): Promise<void> {
    if (this.stockLevelDrafts.length === 0) return

    try {
      await this.stockLevelService.createMany(
        this.stockLevelDrafts.map(draft => ({ ...draft, user_id }))
      )
    } catch (error) {
      this.saveError = error instanceof Error
        ? `Nutzer angelegt, aber die Bestände konnten nicht gespeichert werden: ${error.message}`
        : 'Nutzer angelegt, aber die Bestände konnten nicht gespeichert werden.'
    }
  }

  createUser(): Promise<number> {
    return new Promise<number>(resolve => {
      this.userService.createUser(this.getCreateHash(), this.sendAccount).subscribe(
        user => {
          resolve(user.id)
        },
        error => {
          console.log(error)
          this.saveError = this.extractErrorMessage(error)
          resolve(0)
        }
      )
    })
  }

  /**
   * Fehlermeldung aus der Rails-Antwort ziehen — relevant vor allem beim 422,
   * wenn die gewählte Versandfiliale zwischenzeitlich vergeben wurde.
   */
  private extractErrorMessage(error: unknown): string {
    return httpErrorMessage(error, 'Der Benutzer konnte nicht angelegt werden.')
  }

  createRoles(user_id: number): Promise<void> {
    return new Promise<void>(resolve => {
      const not_crm_roles = this.roles.filter(r => !r.name.includes('CRM'))
      const add_roles_with_ids: number[] = not_crm_roles.filter(r => this.roleIsChecked(r.id)).map(r => r.id)

      this.userService.updateUserRoles(user_id, add_roles_with_ids, []).subscribe(
        _ => {
          resolve()
        },
        error => {
          console.log(error)
          resolve()
        }
      )
    })
  }

  roleIsChecked(id: number): boolean  {
    return !!this.checkedRoles.find(cr => cr.role.id == id && cr.checked)
  }

  // --- Versandfiliale (interner Materialversand) ---

  protected onShippingStoreSelected(store: StorePickerStore): void {
    this.internalShippingStore.set(store)
    this.showStorePicker.set(false)
  }

  protected clearShippingStore(): void {
    this.internalShippingStore.set(null)
  }

  /**
   * Die Versandfilialen sind meist nach dem Techniker benannt, deshalb ist der
   * Name die naheliegende Vorbelegung der Suche. Die Suchbegriffe werden im
   * Backend UND-verknüpft — findet der volle Name nichts, kann im Picker einfach
   * ein Wort gelöscht werden.
   */
  protected get shippingStoreSearchPrefill(): string {
    const controls = this.userForm.controls
    return [controls.first_name.value, controls.last_name.value].filter(Boolean).join(' ')
  }

  /**
   * Bentomax-Techniker sollen immer eine Versandfiliale bekommen. Erkannt wird das
   * an der eingetragenen Firma plus den angehakten Rollen — Getter statt computed(),
   * weil checkedRoles ein einfaches Array mit ngModel ist und kein Signal.
   */
  // Dispatcher- oder Supervisor-Dispatcher-Rolle (ID 4 oder 5) ausgewählt
  protected get hasDispatcherRole(): boolean {
    return this.checkedRoles.some(cr => cr.checked && (cr.role.id === 4 || cr.role.id === 5))
  }

  // Die Tourfarbe färbt Touren auf der Karte — sinnvoll also nur für Techniker.
  protected get hasTechnicianRole(): boolean {
    return this.checkedRoles.some(cr => cr.checked && cr.role.name === 'TECHNICIAN')
  }

  protected get missingInternalShippingStore(): boolean {
    if (this.internalShippingStore()) return false

    return isBentomaxTechnician({
      addressCompany: this.userForm.controls.company.value,
      companyAddressCompany: this.userForm.controls.company_address_company.value,
      roleNames: this.checkedRoles.filter(cr => cr.checked).map(cr => cr.role.name)
    })
  }

  getCreateHash(): TechnicianUpdateHash {
    const controls = this.userForm.controls
    return {
      firstname: controls['first_name'].value,
      lastname: controls['last_name'].value,
      email: controls['email1'].value,
      email2: controls['email2'].value,
      email_moreapp: controls['email_moreapp'].value,
      email_partner: controls['email_partner1'].value,
      email_partner2: controls['email_partner2'].value,
      customer_id: controls['customer_id'].value,
      telephone1: controls['phone1'].value,
      telephone2: controls['phone2'].value,
      telephone_dispatching: controls['telephone_dispatching'].value,
      address_company: controls['company'].value,
      address_street: controls['street'].value,
      address_street_no: '',
      address_city: controls['city'].value,
      address_zip: controls['zip'].value,
      address_country: controls['country'].value,
      address_state: controls['state'].value,
      company_address_company: controls['company_address_company'].value,
      company_address_street: controls['company_address_street'].value,
      company_address_street_no: '',
      company_address_city: controls['company_address_city'].value,
      company_address_zip: controls['company_address_zip'].value,
      company_address_country: controls['company_address_country'].value,
      company_address_state: controls['company_address_state'].value,
      employer_id: controls['employer_id'].value,
      card_nr_packstation: controls['card_nr_packstation'].value,
      pickpoint_nr: controls['pickpoint_nr'].value,
      xml_id: controls['xml_id'].value,
      password: controls['password'].value,
      active: true,
      address_latitude: controls['lat'].value,
      address_longitude: controls['lng'].value,
      comment: controls['comment'].value,
      internal_shipping_store_id: this.internalShippingStore()?.id ?? null,
      tour_color: this.tourColor(),
      dispatcher_color: this.dispatcherColor(),
    }
  }

}
